import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DigitalCase, DigitalSection } from '../../../../redux/types';

type Props = {
  sections: DigitalSection[];
  selectedSection?: DigitalSection;
  cases: DigitalCase[];
  selectedCase?: DigitalCase;
  selectDigitalSection: (sectionId?: number) => void;
  selectDigitalCase: (caseId?: number) => void;
  onSubmitClick: () => void;
  onReviewClick: () => void;
  setShowContextQuestion: (show: boolean) => void;
  disabled?: boolean;
  hasContextQuestions?: boolean;
  showContextQuestion?: boolean;
};

const SectionCaseSelector: React.FC<Props> = ({
  sections,
  selectedSection,
  cases,
  selectedCase,
  selectDigitalSection,
  selectDigitalCase,
  onSubmitClick,
  onReviewClick,
  setShowContextQuestion,
  disabled,
  hasContextQuestions,
  showContextQuestion,
}: Props) => {
  const handleSectionChange = (
    event: SelectChangeEvent<number | string>,
  ) => {
    const secId = event.target.value;
    selectDigitalSection(secId as number);
  };

  const caseSelectHandler = (id: number) => {
    selectDigitalCase(id);
  };

  const submitClickHandler = () => {
    onSubmitClick();
  };

  const renderContextQuestionListItem = () => {
    const key = -Math.abs(selectedSection?.id || 1);
    return (
      <ListItem
        disablePadding
        sx={showContextQuestion ? { backgroundColor: 'primary.light', color: 'white' } : null}
        key={key}
      >
        <ListItemButton
          sx={{ py: 0.5, minHeight: 32 }}
          onClick={() => {
            setShowContextQuestion(true);
            selectDigitalCase(undefined);
          }}
        >
          <ListItemText
            primary={<FormattedMessage id="survey.methodologicalSelector" />}
            primaryTypographyProps={selectedCase?.id === key ? { fontSize: 14, fontWeight: 'medium' } : { fontSize: 14 }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
      }}
    >
      <FormControl
        variant="standard"
        sx={{
          width: '100%',
          mb: 1,
        }}
      >
        <Select
          labelId="select-section"
          id="select-section-bar"
          value={selectedSection?.id || ''}
          onChange={handleSectionChange}
          variant="outlined"
          size="small"
        >
          {
            sections.map((sec) => (
              <MenuItem key={sec.id} value={sec.id}>
                {sec.name}
              </MenuItem>
            ))
          }
        </Select>
      </FormControl>
      {
        selectedSection?.optional && (
          <Alert sx={{ mb: 1 }} severity="info"><FormattedMessage id="survey.optionalSection" /></Alert>
        )
      }
      <Paper
        variant="outlined"
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          height: '60vh',
        }}
      >
        <List>
          {
            cases.map((c) => (
              <ListItem key={c.id} disablePadding sx={c.id === selectedCase?.id ? { backgroundColor: 'primary.light', color: 'white' } : null}>
                <ListItemButton
                  sx={{ py: 0.5, minHeight: 32 }}
                  onClick={() => caseSelectHandler(c.id)}
                >
                  <ListItemText
                    primary={c.name}
                    primaryTypographyProps={c.id === selectedCase?.id ? { fontSize: 14, fontWeight: 'medium' } : { fontSize: 14 }}
                  />
                </ListItemButton>
              </ListItem>
            ))
          }
          {hasContextQuestions && renderContextQuestionListItem()}
        </List>
      </Paper>
      <Box
        sx={{
          mt: 1,
          position: 'sticky',
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifySelf: 'flex-end',
        }}
      >
        <Button
          sx={{ my: 0.5 }}
          variant="outlined"
          onClick={onReviewClick}
        >
          <FormattedMessage id="survey.reviewSubmission" />
        </Button>
        <Button
          disabled={disabled}
          sx={{ my: 0.5 }}
          onClick={submitClickHandler}
          variant="contained"
        >
          <FormattedMessage id="survey.finaliseDialogHeading" />
        </Button>
      </Box>
    </Box>
  );
};

export default SectionCaseSelector;
